import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { User, LoginRequest, AuthContextType } from '../types';
import { LOGIN_ROUTE } from '../constants/routes';
import { message } from 'antd';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_TIMEOUT = parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '900000'); // 15 minutes
const TOKEN_REFRESH_INTERVAL = parseInt(process.env.REACT_APP_TOKEN_REFRESH_INTERVAL || '600000'); // 10 minutes

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Activity tracking
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const [activityWarningShown, setActivityWarningShown] = useState(false);

  /**
   * Update last activity timestamp
   */
  const updateActivity = useCallback(() => {
    const now = Date.now();
    setLastActivity(now);
   // localStorage.setItem('lastActivity', now.toString());
    setActivityWarningShown(false);
  }, []);

  /**
   * Login function
   */
  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    try {
      const response = await authService.login(credentials);
      console.log('Login response:', response);

      // ✅ Tokens are now in HttpOnly cookies (set by server)
      // ✅ Only store user data
      setUser(response.user);
      setIsAuthenticated(true);
      updateActivity();
      message.success('Login successful');
      return true;
    } catch (error: any) {
      message.error(error.message || 'Login failed');
      return false;
    }
  }, [updateActivity]);

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setUser(null);
      setAccessToken(null);
      setIsAuthenticated(false);
      navigate(LOGIN_ROUTE);
      message.info('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [navigate]);

  /**
   * Logout from all devices
   */
  const logoutAll = useCallback(async () => {
    try {
      await authService.logoutAll();
      setUser(null);
      setIsAuthenticated(false);
      navigate(LOGIN_ROUTE);
      message.info('Logged out from all devices');
    } catch (error) {
      console.error('Logout all error:', error);
    }
  }, [navigate]);

  /**
   * Refresh token function
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authService.refreshToken();
      if (result) {
        updateActivity();
        return true;
      }
      console.warn('Token refresh returned null — will retry on next interval');
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }, [updateActivity]);

  /**
   * Auto logout on inactivity
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkActivity = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      const timeUntilLogout = SESSION_TIMEOUT - timeSinceLastActivity;

      // Show warning 1 minute before auto-logout
      if (timeUntilLogout <= 60000 && !activityWarningShown) {
        message.warning('Your session will expire in 1 minute due to inactivity');
        setActivityWarningShown(true);
      }

      // Auto logout if session timeout exceeded
      if (timeSinceLastActivity >= SESSION_TIMEOUT) {
        message.error('Session expired due to inactivity');
        logout();
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(checkActivity);
  }, [isAuthenticated, lastActivity, activityWarningShown, logout]);

  /**
   * Track user activity
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      updateActivity();
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, updateActivity]);

  /**
   * Auto refresh token periodically
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshInterval = setInterval(() => {
      refreshToken();
    }, TOKEN_REFRESH_INTERVAL);

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, refreshToken]);

  /**
   * Initialize auth state
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        // ✅ Try to get current user from server (using HttpOnly cookies)
        let currentUser = await authService.getCurrentUser();

        // The access cookie lives exactly as long as the JWT, while the
        // session and the refresh cookie outlive it. A 401 here can therefore
        // mean "token lapsed, session alive" -- refresh once and ask again
        // before showing the login form to someone who is still signed in.
        if (!currentUser && (await authService.refreshToken())) {
          currentUser = await authService.getCurrentUser();
        }

        if (currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
          updateActivity();
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Failed to initialize auth', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
        // The boot splash in public/index.html covers the blank first paint AND
        // this session check, so the user sees one loader instead of a splash
        // handing over to PublicRoute's spinner.
        (window as any).hideAppSplash?.();
      }
    };

    initAuth();
  }, [updateActivity]);

  /** INITIALIZE AUTH STATE */
//   useEffect(() => {
//   const initAuth = async () => {
//     try {
//       const token = localStorage.getItem('accessToken');
// console.log(token," initializing auth");
//       if (!token) {
//         setIsAuthenticated(false);
//         setUser(null);
//         return;
//       }

//       const storedUser = await authService.getCurrentUser();
//       setUser(storedUser);
//       setIsAuthenticated(true);
//     } catch {
//       localStorage.removeItem('accessToken');
//       setUser(null);
//       setIsAuthenticated(false);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   initAuth();
// }, []);

// useEffect(() => {
//   const initAuth = async () => {
//     try {
//       const storedUser = await authService.getCurrentUser(); // backend reads cookie
//       if (storedUser) {
//         setUser(storedUser);
//         setIsAuthenticated(true);
//       } else {
//         setUser(null);
//         setIsAuthenticated(false);
//       }
//     } catch (error) {
//       console.error('Failed to initialize auth', error);
//       setUser(null);
//       setIsAuthenticated(false);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   initAuth();
// }, [logout]);



  const value: AuthContextType = {
    user,
    accessToken,
    isAuthenticated,
    isLoading,
    login,
    logout,
    logoutAll,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
